import React, { memo } from "react";

import { classNames } from "helpers";
import { ShowcaseBackdrop, ShowcaseCard, ShowcaseNotice, ShowcaseStage } from "ui/components/Showcase";
import Logo from "ui/modules/public/components/Logo/Logo";

import DocumentHeroCard from "./components/DocumentHeroCard";
import StatusDetailCard from "./components/StatusDetailCard";
import StatusNoticeIcon from "./components/StatusNoticeIcon";
import { DOCUMENT_DRAFT_STATUS, DOCUMENT_STATUS_SCENES, STATUS_TONE_CLASSES } from "./consts";
import { DocumentStatusIntroProps } from "./types";

/**
 * Lijevi panel ponude, ugovora i računa: scena prati status dokumenta — predložak, kreiran,
 * poslan, prihvaćen ili potpisan, fiskaliziran, plaćen ili storniran — i objašnjava što status
 * znači i koji je sljedeći korak.
 */
const DocumentStatusIntro: React.FC<DocumentStatusIntroProps> = ({ documentType, isRent = false, statusId }) => {
  const scenes = DOCUMENT_STATUS_SCENES[documentType];
  const statusKey = statusId && scenes[statusId] ? statusId : DOCUMENT_DRAFT_STATUS;
  const scene = scenes[statusKey];
  const statusKeys = Object.keys(scenes).filter((key) => key !== "cancelled");
  const noticeTone = STATUS_TONE_CLASSES[scene.notice.tone];

  return (
    <div className="relative flex flex-col w-full h-full bg-blue-700 overflow-hidden">
      <ShowcaseBackdrop />
      <Logo variant="light" animated className="absolute top-8 left-8 z-10" />

      <div className="relative flex flex-1 min-h-0 items-center justify-center select-none pointer-events-none" aria-hidden="true">
        {/* `key`: promjena statusa iznova montira scenu, pa kartice ponovno ulete. */}
        <ShowcaseStage key={statusKey}>
          <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
            <DocumentHeroCard documentType={documentType} isRent={isRent} scene={scene} statusId={statusKey} />
          </ShowcaseCard>

          <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
            <StatusDetailCard documentType={documentType} isRent={isRent} variant={scene.variant} />
          </ShowcaseCard>

          <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
            <ShowcaseNotice
              title={scene.notice.title}
              subtitle={scene.notice.subtitle}
              iconClassName={noticeTone.iconBox}
              icon={<StatusNoticeIcon name={scene.notice.icon} className={classNames("w-4 h-4", noticeTone.icon)} />}
            />
          </ShowcaseCard>
        </ShowcaseStage>
      </div>

      <div className="relative px-8 pb-12 text-center">
        <div key={statusKey} className="motion-safe:animate-showcaseFadeIn">
          <div className="text-3xl font-bold leading-tight text-white">{scene.title}</div>
          {/* Visina dva retka: opisi su različite duljine, a scena iznad ne smije skakati. */}
          <p className="mt-3 mx-auto max-w-md min-h-[3rem] text-base text-blue-100">{scene.description}</p>
        </div>
        {/* Točkice prate redoslijed statusa; storno je izvan redoslijeda pa ga nema. */}
        <div className="flex items-center justify-center gap-2 mt-6">
          {statusKeys.map((key) => (
            <span
              key={key}
              className={classNames(
                "h-1.5 rounded-full transition-all duration-300",
                key === statusKey ? "w-6 bg-amber-400" : "w-1.5 bg-white/40"
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default memo(DocumentStatusIntro);
